import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";
import SectionHeading from "@/components/base/SectionHeading";
import type { DirectoryLocation } from "@/types/directory";
import { getDistricts } from "@/services/directoryService";

const ABOUT_HERO =
  "https://readdy.ai/api/search-image?query=Warm%20stylised%20editorial%20illustration%20of%20the%20coastal%20Karnataka%20shoreline%20with%20traditional%20fishing%20boats%2C%20coconut%20palms%2C%20terracotta%20rooftops%20and%20a%20golden%20sunset%20over%20the%20Arabian%20Sea%2C%20rich%20warm%20amber%20tones%2C%20artistic%20travel%20poster%20style%20with%20soft%20paper%20texture&width=1600&height=900&seq=cbc-about-hero-01&orientation=landscape";

const STORY_IMAGE =
  "https://readdy.ai/api/search-image?query=Local%20small%20business%20owner%20in%20coastal%20Karnataka%20smiling%20at%20his%20colourful%20storefront%20with%20fresh%20produce%20and%20palm%20trees%2C%20warm%20natural%20light%2C%20authentic%20documentary%20photography&width=1000&height=750&seq=cbc-about-story-01&orientation=landscape";

const stats = [
  { value: "4,080+", label: "Businesses listed" },
  { value: "3", label: "Districts covered" },
  { value: "45+", label: "Categories" },
  { value: "120K+", label: "Monthly visitors" },
];

const values = [
  {
    icon: "ri-shield-check-line",
    title: "Local first",
    description:
      "We exist for businesses on the Karnataka coast — built by people who live here and understand its towns, languages and trade.",
  },
  {
    icon: "ri-focus-3-line",
    title: "Honest by default",
    description:
      "Sponsored placements are always labelled. Reviews are moderated. You always know what you are looking at.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "Business-friendly",
    description:
      "Free to start, transparent pricing and no lock-in. Local traders should grow online without being squeezed.",
  },
  {
    icon: "ri-signal-tower-line",
    title: "Built to scale",
    description:
      "A platform designed to keep working as more businesses and more customers join from across the three districts.",
  },
];

export default function AboutPage() {
  const [districts, setDistricts] = useState<DirectoryLocation[]>([]);

  useEffect(() => {
    let active = true;
    getDistricts()
      .then((list) => {
        if (active) setDistricts(list);
      })
      .catch(() => {
        if (active) setDistricts([]);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <SiteLayout>
      <Seo
        title="About Coastal Bizz Connect"
        description="Learn about Coastal Bizz Connect, the trusted hyperlocal business directory connecting customers with verified businesses across South Canara, Udupi and North Canara in coastal Karnataka."
        keywords="about Coastal Bizz Connect, coastal Karnataka business directory, South Canara Udupi North Canara local businesses"
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          description:
            "Hyperlocal business directory for South Canara, Udupi and North Canara, connecting customers with verified local businesses.",
          areaServed: ["South Canara", "Udupi", "North Canara"],
          address: {
            "@type": "PostalAddress",
            addressLocality: "Mangaluru",
            addressRegion: "Karnataka",
            addressCountry: "IN",
          },
        }}
      />

      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={ABOUT_HERO}
            alt="Stylised illustration of the coastal Karnataka shoreline at sunset"
            title="About Coastal Bizz Connect"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/40 via-primary-950/30 to-primary-950/60" />
        </div>
        <div className="relative mx-auto w-full max-w-[1400px] px-6 py-16 md:py-24">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-accent-300">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-background-100">About</span>
          </nav>
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            Connecting the coast, one business at a time
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            Coastal Bizz Connect is the trusted hyperlocal directory for South Canara, Udupi and North
            Canara — helping people find the right local business and helping local businesses be
            found.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-secondary-700">
              <span className="h-1 w-1 rounded-full bg-accent-500" />
              Our story
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
              A directory made for coastal Karnataka
            </h2>
            <div className="mt-4 flex flex-col gap-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              <p>
                From the busy commercial streets of Mangaluru to the temple town of Udupi and the
                scenic bays of Karwar, the Karnataka coast runs on thousands of small, hardworking
                businesses. Too many of them were still invisible online — relying on word of mouth
                alone to find new customers.
              </p>
              <p>
                Coastal Bizz Connect was built to change that. We bring verified local businesses
                together in one trusted, well-organised directory, so customers can search by category
                and location and connect directly — by phone, WhatsApp or enquiry — without any
                middlemen.
              </p>
              <p>
                For business owners, we keep it simple: start with a free listing and upgrade only when
                you see the value. No lock-in, no surprises.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-100 px-4 py-5 text-center"
                >
                  <p className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="h-72 w-full overflow-hidden rounded-lg border border-background-200 md:h-[440px]">
            <img
              src={STORY_IMAGE}
              alt="A local shop owner at their storefront in coastal Karnataka"
              title="Local businesses of coastal Karnataka"
              className="h-full w-full object-top"
            />
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-16">
          <SectionHeading
            eyebrow="Where we cover"
            title="Three districts, every town in between"
            description="We go deeper than a city-level listing — right down to the town and locality, so customers find businesses that are actually nearby."
          />

          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {districts.map((district) => (
              <Link
                key={district.id}
                to={`/location/${district.slug}`}
                className="group relative block h-56 overflow-hidden rounded-lg border border-background-200"
              >
                <img
                  src={district.image}
                  alt={`${district.name} district in coastal Karnataka`}
                  title={`${district.name} - Coastal Bizz Connect`}
                  className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/25 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <h3 className="font-heading text-xl font-bold text-background-50">
                    {district.name}
                  </h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-background-200">
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-store-3-line text-[13px]" />
                    </span>
                    {district.businessCount.toLocaleString("en-IN")} businesses listed
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-16">
        <SectionHeading
          eyebrow="What we stand for"
          title="Principles that keep the directory trustworthy"
          description="These are the things we refuse to compromise on as the platform grows."
          align="center"
        />
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((value) => (
            <article
              key={value.title}
              className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className={`${value.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {value.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-primary-900">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center gap-6 px-6 py-14 text-center md:py-16">
          <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
            Be part of the coast&apos;s business community
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            Whether you are looking for a local service or want your business found, Coastal Bizz
            Connect is built for you.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/list-your-business"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-accent-600"
            >
              List your business
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-700 px-6 py-3 text-sm font-semibold text-background-100 transition-colors duration-200 hover:border-accent-400 hover:text-accent-300"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}